import { computed, ref, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import * as socialListeningApi from '@/apis/social-listening';
import { logger } from '@/utils/logger';
import { ORGANIZATION } from '@/models/auth/permissions.enum';
import { useIdentityStore } from '@/stores/identity';

export const useDashboardsSocialListeningStore = defineStore('dashboardsSocialListening', () => {
  const identityStore = useIdentityStore();

  const topicList = ref([]);
  const comparisonList = ref([]);
  const topicListByOrganization = ref({});
  const comparisonListByOrganization = ref({});
  const socialListeningLocations = shallowRef([]);

  const brands = computed(() => Object.values(identityStore.identity?.brands ?? {}));
  const organizationId = computed(() => identityStore.currentBrand?.organizationId);

  function getOrganizationDetails(orgId) {
    return brands.value.find((brand) => brand.organization?.id === Number(orgId))?.organization;
  }

  function mapListToType(listByOrganization, type) {
    return Object.entries(listByOrganization).reduce((acc, [orgId, items]) => {
      const organizationDetails = getOrganizationDetails(orgId);

      acc.push(
        ...items.map((item) => ({
          ...item,
          type,
          organizationDetails,
        })),
      );
      return acc;
    }, []);
  }

  function groupListsWithOrganizationDetails(listByOrganization) {
    return Object.entries(listByOrganization).reduce((acc, [orgId, list]) => {
      if (list?.length > 0) {
        const organizationDetails = getOrganizationDetails(orgId);
        if (organizationDetails) {
          acc.push({
            organizationDetails,
            list,
          });
        }
      }
      return acc;
    }, []);
  }

  const topicListForMultipleOrganizations = computed(() =>
    mapListToType(topicListByOrganization.value, 'topic'),
  );
  const topicListAndOrganizationDetails = computed(() =>
    groupListsWithOrganizationDetails(topicListByOrganization.value),
  );

  const comparisonListForMultipleOrganizations = computed(() =>
    mapListToType(comparisonListByOrganization.value, 'comparison'),
  );

  const comparisonListAndOrganizationDetails = computed(() =>
    groupListsWithOrganizationDetails(comparisonListByOrganization.value),
  );

  const organizationIdsWithSLPermission = computed(() =>
    Array.from(
      brands.value.reduce(
        (acc, brand) =>
          identityStore.checkIfOrgPermissionsExist(
            [ORGANIZATION.LISTENING.CAN_ACCESS_SOCIAL_LISTENING],
            [brand],
          )
            ? acc.add(brand.organizationId)
            : acc,
        new Set(),
      ),
    ),
  );

  const organizationsWithSLPermission = computed(() =>
    Array.from(
      brands.value.reduce(
        (acc, brand) =>
          identityStore.checkIfOrgPermissionsExist(
            [ORGANIZATION.LISTENING.CAN_ACCESS_SOCIAL_LISTENING],
            [brand],
          )
            ? acc.add(brand.organization)
            : acc,
        new Set(),
      ),
    ),
  );

  async function getSavedTopics() {
    try {
      const res = await socialListeningApi.getTopics(organizationId.value, true);
      topicList.value = res.data.data;
    } catch (e) {
      logger.error(`Failed to fetch topics for organization ${organizationId.value}: ${e.message}`);
      topicList.value = [];
    }
  }

  async function getComparisonList() {
    try {
      const response = await socialListeningApi.getComparisons(organizationId.value);
      comparisonList.value = response.data.data;
    } catch {
      comparisonList.value = [];
    }
  }

  async function getSavedTopicsForMultipleOrganizations() {
    const results = await Promise.allSettled(
      organizationIdsWithSLPermission.value.map((orgId) =>
        socialListeningApi.getTopics(orgId, true),
      ),
    );
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        topicListByOrganization.value[organizationIdsWithSLPermission.value[index]] =
          result.value.data.data;
      } else {
        logger.error(
          `Failed to fetch topics for organization ${organizationIdsWithSLPermission.value[index]}: ${result.reason.message}`,
        );
        delete topicListByOrganization.value[organizationIdsWithSLPermission.value[index]];
      }
    });
  }

  async function getComparisonTopicsForMultipleOrganizations() {
    const results = await Promise.allSettled(
      organizationIdsWithSLPermission.value.map((orgId) =>
        socialListeningApi.getComparisons(orgId),
      ),
    );
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        comparisonListByOrganization.value[organizationIdsWithSLPermission.value[index]] =
          result.value.data.data;
      } else {
        logger.error(
          `Failed to fetch comparisons for organization ${organizationIdsWithSLPermission.value[index]}: ${result.reason.message}`,
        );
        delete comparisonListByOrganization.value[organizationIdsWithSLPermission.value[index]];
      }
    });
  }

  async function getSocialListeningLocations() {
    const url = new URL(
      '@/assets/datasets/social_listening_city_state_options.json',
      import.meta.url,
    ).href;
    const response = await fetch(url);
    socialListeningLocations.value = await response.json();
  }

  return {
    comparisonList,
    comparisonListByOrganization,
    comparisonListForMultipleOrganizations,
    comparisonListAndOrganizationDetails,
    socialListeningLocations,
    topicList,
    topicListByOrganization,
    topicListForMultipleOrganizations,
    topicListAndOrganizationDetails,
    getSavedTopics,
    getSavedTopicsForMultipleOrganizations,
    getComparisonList,
    getComparisonTopicsForMultipleOrganizations,
    getSocialListeningLocations,
  };
});
